import React, { useEffect, useState } from "react";

// region External libraries
import _ from "lodash";
import { Button } from "@npm-telluria-tecnologia/telluria-ui";
import { Checkbox, FormControl, FormControlLabel, FormGroup } from "@mui/material";
// endregion External libraries
// region Helpers
import { EColors } from "src/helpers/utils";
import { claimGroupObject, EClaimGroupModule } from "src/helpers/claimGroup";
import { claimObject, EClaimModule } from "src/helpers/claim";
// endregion Helpers
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Atoms
import AccordionSummaryChips, { IChipProps } from "@atoms/AccordionSummaryChips";
// endregion Atoms
// region Molecules
import KanbanAccordionCard from "@molecules/KanbanAccordionCard";
// endregion Molecules
// region Organisms
import { EPermissionGroupOrigin } from "src/organisms/PermissionGroup";
// endregion Organisms
// region Pages
// endregion Pages
// region Store
import { ClaimGroupType, RoleClaimType } from "src/store/graphql/helpers/types.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IClaimGroupProps {
  claimGroup: ClaimGroupType;
  selectedClaimsOfCurrentRole?: RoleClaimType[];
  selectAllClaimsFromClaimGroups: boolean;
  onClaimsChange: (permissionIds: string[], value: boolean) => void;
  origin: EPermissionGroupOrigin;
  selectedClaims?: {claimId: string}[];
}

export interface IOption {
  id: string;
  title: string;
  chipText: string;
  order: number;
  value: boolean;
  claimValueOfCurrentRole?: boolean;
}

const ClaimGroup: React.FC<IClaimGroupProps> = (
  { claimGroup, selectedClaimsOfCurrentRole, selectAllClaimsFromClaimGroups, onClaimsChange, origin, selectedClaims }
) => {
  // region States
  const [chips, setChips] = useState<IChipProps[]>([]);
  const [options, setOptions] = useState<IOption[]>([]);
  const [expandedCard, setExpandedCard] = useState<boolean>(false);
  const [selectAllOptions, setSelectAllOptions] = useState<boolean>(false);
  const [roleExclusiveOptions, setRoleExclusiveOptions] = useState<IOption[]>([]);
  // endregion States

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks

  // region Functions
  const markOptionAsRoleExclusive = (checkedOptions: IOption[], checked: boolean[]) => {
    let roleExclusiveOptionsClone = _.cloneDeep(roleExclusiveOptions);

    checkedOptions.forEach((checkedOption, index) => {
      if (checked[index] && checkedOption.claimValueOfCurrentRole) {
        roleExclusiveOptionsClone = roleExclusiveOptionsClone.filter((range) => range.id !== checkedOption.id);
      } else if (!checked[index] && checkedOption.claimValueOfCurrentRole) {
        roleExclusiveOptionsClone.push(checkedOption);
      }
    });

    setRoleExclusiveOptions(roleExclusiveOptionsClone);
  };

  const checkIfAllOptionsAreSelected = (chekedOptionId: string, checked: boolean) => {
    let selectedClaimsClone = _.cloneDeep(selectedClaims) || [];

    if (checked) {
      selectedClaimsClone = [...selectedClaimsClone, { claimId: chekedOptionId }];
    } else {
      selectedClaimsClone = selectedClaimsClone?.filter((selectedClaim) => selectedClaim.claimId !== chekedOptionId);
    }

    const allIdsFound = options
      .every((option) => selectedClaimsClone?.some((selectedClaim) => selectedClaim.claimId === option.id));

    setSelectAllOptions(allIdsFound);
  };

  const getChips = () => {
    const chips = [] as IChipProps[];

    const selectedOptions = options
      .filter((option) => selectedClaims?.some((selectedClaim) => selectedClaim.claimId === option.id));

    if (origin === EPermissionGroupOrigin.ROLE) {
      return selectedOptions.map((option) => (
        {
          id: option.id,
          label: option.chipText,
          showDeleteIcon: true
        }
      )) as IChipProps[];
    }

    selectedOptions.forEach((option) => {
      const color = option?.claimValueOfCurrentRole ? EColors.DEFAULT : EColors.SUCCESS;

      chips.push({
        id: option.id,
        label: option.chipText,
        showDeleteIcon: true,
        color
      } as IChipProps);
    });

    roleExclusiveOptions.forEach((option) => {
      chips.push({
        id: option.id,
        label: option.chipText,
        showDeleteIcon: false,
        color: EColors.WARNING
      } as IChipProps);
    });

    return chips;
  };
  // endregion Functions

  // region Handlers
  const handleAccordionCardChange = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCard(isExpanded);
  };

  const handleOptionCheck = (checkedOption: IOption, checked: boolean) => {
    markOptionAsRoleExclusive(Array.of(checkedOption), Array.of(checked));

    checkIfAllOptionsAreSelected(checkedOption.id, checked);

    onClaimsChange(Array.of(checkedOption.id), checked);
  };

  const handleClickSelectAllOptions = () => {
    const updatedSelectAllOptions = !selectAllOptions;

    setSelectAllOptions(updatedSelectAllOptions);

    markOptionAsRoleExclusive(options, new Array(options.length).fill(updatedSelectAllOptions));

    onClaimsChange(options.map((option) => option.id), updatedSelectAllOptions);
  };

  const handleDeleteOption = (optionId: string) => {
    const correspondingOption = options.find((option) => option.id === optionId) as IOption;

    markOptionAsRoleExclusive(Array.of(correspondingOption), Array.of(false));
    setSelectAllOptions(false);

    onClaimsChange(Array.of(optionId), false);
  };
  // endregion Handlers

  useEffect(() => {
    const allIdsSelected = options
      .every((option) => selectedClaims?.some((selectedClaim) => selectedClaim.claimId === option.id));

    setSelectAllOptions(allIdsSelected);
  }, [selectedClaims]);

  useEffect(() => {
    setChips(getChips());
  }, [selectedClaims, roleExclusiveOptions]);

  useEffect(() => {
    markOptionAsRoleExclusive(options, new Array(options.length).fill(selectAllClaimsFromClaimGroups));
  }, [selectAllClaimsFromClaimGroups]);

  useEffect(() => {
    const claimOptions = claimGroup.claims?.map((claim) => ({
      id: claim?.id,
      title: claimObject[claim?.alias as EClaimModule]?.title ?? "error",
      chipText: claimObject[claim?.alias as EClaimModule]?.title ?? "error",
      order: claim?.order,
      value: selectedClaims?.some((selectedClaim) => selectedClaim.claimId === claim?.id),
      claimValueOfCurrentRole: selectedClaimsOfCurrentRole?.some((selectedClaim) => selectedClaim.claimId === claim?.id)
    })) as IOption[];

    const allOptionsSelected = claimOptions.every((option) => option.value);

    setOptions(_.sortBy(claimOptions, "order"));
    setSelectAllOptions(allOptionsSelected);

    markOptionAsRoleExclusive(claimOptions, claimOptions.map((option) => option.value));
  }, []);

  return (

    <Styled.Container>
      <KanbanAccordionCard
        onChange={handleAccordionCardChange}
        accordionSummary={(
          <AccordionSummaryChips
            label={claimGroupObject[claimGroup?.alias as EClaimGroupModule].title}
            chips={chips}
            onDeleteChip={handleDeleteOption}
          />
        )}
        showExpandIcon
        expanded={expandedCard}
      >
        <Styled.AccordionDetail>
          <Button
            type="button"
            className="select-all-button"
            text={!selectAllOptions
              ? t(GlobalMessages.selectAllText)
              : t(GlobalMessages.removeAllText)}
            disableRipple
            onClick={handleClickSelectAllOptions}
          />
          <FormControl fullWidth className="form-control_values">
            <FormGroup>
              {
                options.length > 0 && options.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    control={(
                      <Checkbox
                        checked={selectedClaims?.some((selectedClaim) => selectedClaim.claimId === item.id)}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          event.stopPropagation();

                          const { checked } = event.target;

                          handleOptionCheck(item, checked);
                        }}
                        size="small"
                      />
                    )}
                    disableTypography
                    label={(
                      <Styled.Label
                        color={chips.find((chip) => chip.id === item.id)?.color}
                      >
                        {t(item.title)}
                      </Styled.Label>
                    )}
                  />
                ))
              }
            </FormGroup>
          </FormControl>
        </Styled.AccordionDetail>
      </KanbanAccordionCard>
    </Styled.Container>
  );
};

ClaimGroup.defaultProps = {
  selectedClaimsOfCurrentRole: undefined,
  selectedClaims: undefined
};

export default ClaimGroup;
